import React from 'react';
import { Box, Tab, styled } from '@mui/material';
import Tabs from '@mui/material/Tabs';
import { useTranslation } from 'react-i18next';
import { tabPanelProps } from '../SingleClient/SingleClient';

interface IStyledTabProps {
  isSelected: boolean;
  isrtl?: boolean;
}

const StyledTab = styled(Tab)<IStyledTabProps>(({ theme, isSelected, isrtl }) => ({
  minHeight: '56px',
  padding: '16px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
  fontFamily: 'Satoshi',
  fontSize: '17px',
  fontWeight: 400,
  lineHeight: '22px',
  color: theme.palette.primary.main,
  '&.Mui-selected': {
    fontWeight: 700,
    transition: 'font-weight 0.2s ease-in-out',
    background:
      'linear-gradient(138deg, rgba(101, 153, 165, 0.15) 24.11%, rgba(59, 102, 126, 0.15) 83.2%), #FFF',
    '& span': {
      background: 'linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
  },
  '&:not(:first-of-type)': {
    borderLeft: isrtl ? 'none' : '0.5px solid #7CAEBE',
    borderRight: isrtl ? '0.5px solid #7CAEBE' : 'none',
  },
  '& .MuiTab-iconWrapper': {
    color: isSelected ? '#6599A5' : '#FFFFFF',
  },
}));

type TeamMembersTabsProps = {
  value: number;
  setValue: (_value: number) => void;
  isrtl?: boolean;
};

function TeamMembersTabs({ value, setValue }: TeamMembersTabsProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const tabsProperties = [
    {
      label: 'team.activeMembers',
      selectedIcon: '/assets/icons/check-circle-bold.svg',
      nonSelectedIcon: '/assets/icons/check-circle.svg',
    },
    {
      label: 'team.archivedMembers',
      selectedIcon: '/assets/icons/archive-box-bold.svg',
      nonSelectedIcon: '/assets/icons/archive-box.svg',
    },
  ];

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box borderRadius="10px" border="0.5px solid #7CAEBE" justifyContent="center" mt={3} mb={2}>
      <Tabs
        value={value}
        onChange={handleChange}
        aria-label="Team Members Tabs"
        variant="fullWidth"
        sx={{
          width: '100%',
          minHeight: '56px',
          '& .MuiTabs-indicator': {
            display: 'none',
          },
        }}
      >
        {tabsProperties.map((singleTab, index) => (
          <StyledTab
            key={singleTab.label}
            label={
              <Box display="flex" alignItems="center" gap="8px">
                <img
                  src={value === index ? singleTab.selectedIcon : singleTab.nonSelectedIcon}
                  alt={`${singleTab.label} Icon`}
                  style={{ width: 24, height: 24 }}
                />
                <span>{t(singleTab.label)}</span>
              </Box>
            }
            isrtl={language.split('-')[0] === 'ar'}
            sx={{
              borderRadius: () => {
                if (index === 0) {
                  return language.split('-')[0] === 'ar' ? '0 10px 10px 0' : '10px 0 0 10px';
                }
                if (index === tabsProperties.length - 1) {
                  return language.split('-')[0] === 'ar' ? '10px 0 0 10px' : '0 10px 10px 0';
                }
                return '0';
              },
            }}
            isSelected={value === index}
            {...tabPanelProps(index)}
          />
        ))}
      </Tabs>
    </Box>
  );
}

export default TeamMembersTabs;
